// Reporting dates use Central time even when the administrator's browser does not.
export const REPORTING_ZONE = 'America/Chicago';
const formatter = new Intl.DateTimeFormat('en-US', { timeZone: REPORTING_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });
export const calendarDate = date => date.toISOString().slice(0, 10);
const validDate = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && calendarDate(new Date(value)) === value;
export function centralDate(value) {
  if (!value) return '';
  // A date-only input is already a calendar date, not midnight UTC.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return validDate(value) ? value : '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = Object.fromEntries(formatter.formatToParts(date).map(part => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}
export const shiftDate = (value, days) => calendarDate(new Date(Date.parse(`${value}T12:00:00Z`) + days * 86400000));
export const weekday = value => new Date(`${value}T12:00:00Z`).getUTCDay();
export const weekdayName = value => new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`));
export const CALENDARS = { federal: 'U.S. federal holidays', texas: 'Texas state holidays' };
const holidayCache = new Map();
const nth = (year, month, day, number) => {
  const first = `${year}-${String(month).padStart(2, '0')}-01`;
  return shiftDate(first, (day - weekday(first) + 7) % 7 + 7 * (number - 1));
};

// Sources: https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/
// https://www.tsl.texas.gov/ref/abouttx/holidays
// Reference schedules, not an assertion of private-company closures. Texas optional
// religious holidays are not automatically treated as company-wide holidays.
export function holidaysForYear(year, calendar = 'federal') {
  const key = `${calendar}:${year}`;
  if (holidayCache.has(key)) return holidayCache.get(key);
  const dates = {};
  const add = (date, name) => { if (date.startsWith(`${year}-`)) (dates[date] ||= []).push(name); };
  // Adjacent years include an observed New Year's Day on December 31.
  for (const y of [year - 1, year, year + 1]) {
    const fixed = [['01-01', "New Year's Day"], ['07-04', 'Independence Day'], ['11-11', 'Veterans Day'], ['12-25', 'Christmas Day']];
    if (y >= 2021 || calendar === 'texas') fixed.push(['06-19', calendar === 'texas' ? 'Emancipation Day in Texas (partial staffing)' : 'Juneteenth']);
    if (calendar === 'texas') fixed.push(['01-19', 'Confederate Heroes Day (partial staffing)'], ['03-02', 'Texas Independence Day (partial staffing)'], ['04-21', 'San Jacinto Day (partial staffing)'], ['08-27', 'Lyndon Baines Johnson Day (partial staffing)'], ['12-24', 'Christmas Eve'], ['12-26', 'Day after Christmas']);
    for (const [monthDay, name] of fixed) {
      const date = `${y}-${monthDay}`;
      add(date, name);
      // Texas state holidays falling on weekends do not get substitute weekdays.
      if (calendar === 'federal' && [0, 6].includes(weekday(date))) add(shiftDate(date, weekday(date) === 6 ? -1 : 1), `${name} (observed)`);
    }
    add(nth(y, 1, 1, 3), 'Martin Luther King Jr. Day');
    add(nth(y, 2, 1, 3), "Presidents' Day");
    const june = `${y}-06-01`;
    add(shiftDate(june, -((weekday(june) + 6) % 7 || 7)), 'Memorial Day');
    add(nth(y, 9, 1, 1), 'Labor Day');
    if (calendar === 'federal') add(nth(y, 10, 1, 2), 'Columbus Day');
    const thanksgiving = nth(y, 11, 4, 4);
    add(thanksgiving, 'Thanksgiving Day');
    if (calendar === 'texas') add(shiftDate(thanksgiving, 1), 'Day after Thanksgiving');
  }
  // Bound the cache for long-lived admin sessions.
  if (holidayCache.size >= 64) holidayCache.clear();
  holidayCache.set(key, dates);
  return dates;
}
export function dateInfo(date, calendar = 'federal', today = centralDate(new Date())) {
  return { date, weekday: weekdayName(date), weekend: [0, 6].includes(weekday(date)),
    holidays: holidaysForYear(Number(date.slice(0, 4)), calendar)[date] || [], incomplete: date === today };
}
export function registrationCalendar(tenants, range, { excludeWeekends = false, excludeHolidays = false, calendar = 'federal', grain = 'day' } = {}, now = new Date()) {
  const today = centralDate(now);
  const knownDates = tenants.map(tenant => centralDate(tenant.created_at)).filter(date => date && date <= today).sort();
  const start = range.start || knownDates[0] || range.end || today;
  const end = range.end && range.end < today ? range.end : today;
  if (!validDate(start) || !validDate(end) || (range.end && !validDate(range.end)) || Date.parse(end) - Date.parse(start) > 36600 * 86400000) {
    return { dates: [], buckets: [], tenants: [], weekdayAverages: [], today, start, end, error: 'Choose valid calendar dates spanning no more than 100 years.' };
  }
  const dates = [];
  // No future dates, even when a custom range extends beyond today.
  for (let date = start; date <= end; date = shiftDate(date, 1)) {
    const info = dateInfo(date, calendar, today);
    if ((!excludeWeekends || !info.weekend) && (!excludeHolidays || !info.holidays.length)) dates.push({ ...info, tenants: 0, organizations: 0, files: 0 });
  }
  const byDate = new Map(dates.map(info => [info.date, info]));
  const included = tenants.filter(tenant => byDate.has(centralDate(tenant.created_at)));
  for (const tenant of included) {
    const info = byDate.get(centralDate(tenant.created_at));
    info.tenants++;
    info.organizations += Number(tenant.organizations_count || 0);
    info.files += Number(tenant.files_count || 0);
  }
  const grouped = new Map();
  for (const info of dates) {
    const key = grain === 'month' ? `${info.date.slice(0, 7)}-01` : grain === 'week' ? shiftDate(info.date, -((weekday(info.date) + 6) % 7)) : info.date;
    if (!grouped.has(key)) grouped.set(key, { date: key, days: [], tenants: 0, organizations: 0, files: 0 });
    const group = grouped.get(key); group.days.push(info);
    for (const metric of ['tenants', 'organizations', 'files']) group[metric] += info[metric];
  }
  const weekdayAverages = [1, 2, 3, 4, 5, 6, 0].map(day => {
    const complete = dates.filter(info => weekday(info.date) === day && !info.incomplete);
    return { weekday: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][day],
      days: complete.length, average: complete.length ? complete.reduce((sum, info) => sum + info.tenants, 0) / complete.length : null };
  });
  return { dates, buckets: [...grouped.values()], tenants: included, weekdayAverages, today, start, end };
}

// Chart.js category-axis bands retain an equal width for each included calendar day.
export const calendarBands = {
  id: 'registrationCalendarBands',
  beforeDatasetsDraw(chart, args, options) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x || !options.days) return;
    ctx.save();
    options.days.forEach((info, index) => {
      if (!info.weekend && !info.holidays.length) return;
      const center = scales.x.getPixelForValue(index);
      const previous = index ? scales.x.getPixelForValue(index - 1) : chartArea.left;
      const next = index + 1 < options.days.length ? scales.x.getPixelForValue(index + 1) : chartArea.right;
      const left = index ? (previous + center) / 2 : chartArea.left;
      const right = index + 1 < options.days.length ? (center + next) / 2 : chartArea.right;
      ctx.fillStyle = info.holidays.length ? 'rgba(229, 170, 45, 0.16)' : 'rgba(128, 140, 160, 0.12)';
      ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
    });
    ctx.restore();
  },
};
